import React from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import useBottomUrlBarDetection from '../hooks/useBottomUrlBarDetection';

interface StorageQuotaDisplayProps {
  usagePercentage: number;
  usedSize: number;
  totalSize: number;
  loading?: boolean;
}

const StorageQuotaDisplay: React.FC<StorageQuotaDisplayProps> = ({
  usagePercentage,
  usedSize,
  totalSize,
  loading = false
}) => {
  const { darkMode } = useDarkMode();
  const [showDetails, setShowDetails] = React.useState(false);
  const { isBottomUrlBarVisible, isMobile } = useBottomUrlBarDetection();

  // Calculate bottom position based on URL bar visibility
  const bottomPosition = React.useMemo(() => {
    // Start with a higher default position
    const defaultPosition = isMobile ? '100px' : '24px';
    
    // If URL bar is visible on mobile, position even higher
    if (isMobile && isBottomUrlBarVisible) {
      return '160px';
    }
    
    return defaultPosition;
  }, [isMobile, isBottomUrlBarVisible]);

  // Format size to human readable format
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  // Determine color based on usage percentage
  const getColorClass = (percentage: number): string => {
    if (percentage >= 90) return 'bg-red-400';
    if (percentage >= 70) return 'bg-yellow-400';
    return 'bg-blue-400';
  };

  if (loading) {
    return (
      <div 
        className="fixed right-4"
        style={{ 
          bottom: bottomPosition,
          transition: 'bottom 0.3s ease-in-out' 
        }}
      >
        <div className={`
          p-2 rounded-lg 
          ${darkMode ? 'bg-gray-800/70' : 'bg-white/70'} 
          backdrop-blur-sm shadow-sm 
          opacity-60
        `}>
          <div className="w-32 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-gray-300 dark:bg-gray-600 animate-pulse rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`group fixed transition-all duration-200 ${
        showDetails ? 'w-64' : 'w-32'
      }`}
      style={{ 
        bottom: bottomPosition, 
        right: '16px',
        transition: 'bottom 0.3s ease-in-out'
      }}
      onMouseEnter={() => setShowDetails(true)}
      onMouseLeave={() => setShowDetails(false)}
    >
      <div className={`
        relative p-2 rounded-lg 
        ${darkMode ? 'bg-gray-800/70' : 'bg-white/70'} 
        backdrop-blur-sm shadow-sm 
        transition-all duration-200
        ${showDetails ? 'opacity-100' : 'opacity-60 hover:opacity-100'}
      `}>
        {/* Compact View */}
        <div className={`flex items-center gap-2 ${showDetails ? 'mb-2' : ''}`}>
          <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full ${getColorClass(usagePercentage)} transition-all duration-300`}
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            />
          </div>
          <span className="text-xs whitespace-nowrap">{usagePercentage.toFixed(0)}%</span>
        </div>

        {/* Expanded Details */}
        {showDetails && (
          <div className="text-xs text-gray-500 dark:text-gray-400 pt-1 border-t dark:border-gray-700">
            <div className="flex justify-between items-center">
              <span>{formatSize(usedSize)} used</span>
              <span>of {formatSize(totalSize)}</span>
            </div>
          </div>
        )}

        {/* Info Icon */}
        <div className={`
          absolute -left-2 -top-2 
          opacity-0 group-hover:opacity-100 
          transition-opacity duration-200
        `}>
          <InformationCircleIcon className="w-4 h-4 text-gray-400" />
        </div>
      </div>
    </div>
  );
};

export default StorageQuotaDisplay; 